import { useQuery } from '@tanstack/react-query'
import { Clock, AlertTriangle, X } from 'lucide-react'
import { useState } from 'react'
import { subscriptionApi } from '../../services/api'

/**
 * Banner showing trial status and days remaining.
 * Shows warning style when trial is expiring soon (<=5 days).
 * Can be dismissed for the session.
 */
export default function TrialBanner() {
  const [isDismissed, setIsDismissed] = useState(false)

  const { data: subscription } = useQuery({
    queryKey: ['subscription-current'],
    queryFn: () => subscriptionApi.getCurrent().then((r) => r.data),
    staleTime: 60000, // 1 minute
    retry: 1,
  })

  // Don't show if not on trial, dismissed, or no data
  if (!subscription || subscription.status !== 'Trial' || isDismissed) {
    return null
  }

  const daysLeft = subscription.daysUntilTrialEnds ?? 0
  const isExpiringSoon = subscription.isTrialExpiringSoon

  // Format trial end date
  const trialEndDate = subscription.trialEndsAt
    ? new Date(subscription.trialEndsAt).toLocaleDateString('pl-PL', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null

  if (isExpiringSoon) {
    // Urgent warning style - trial ending soon
    return (
      <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <div>
              <span className="font-medium">
                {daysLeft <= 0
                  ? 'Twój okres próbny wygasa dzisiaj!'
                  : daysLeft === 1
                    ? 'Został 1 dzień okresu próbnego!'
                    : `Zostało ${daysLeft} dni okresu próbnego!`}
              </span>
              <span className="hidden sm:inline text-orange-100 ml-2">
                {trialEndDate && `Kończy się ${trialEndDate}.`}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="https://www.lextool.pl/cennik"
              className="bg-white text-orange-600 hover:bg-orange-50 px-4 py-1.5 rounded-lg font-medium text-sm transition-colors whitespace-nowrap"
            >
              Wybierz plan
            </a>
            <button
              onClick={() => setIsDismissed(true)}
              className="text-orange-200 hover:text-white p-1"
              aria-label="Zamknij"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Normal info style - trial active with time remaining
  return (
    <div className="bg-blue-600 text-white px-4 py-2.5">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 flex-shrink-0" />
          <span>
            <span className="font-medium">Okres probny</span>
            <span className="text-blue-200 ml-2">
              {daysLeft} {daysLeft === 1 ? 'dzien' : 'dni'} do konca
              {trialEndDate && ` (${trialEndDate})`}
            </span>
          </span>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="https://www.lextool.pl/cennik"
            className="bg-white/20 hover:bg-white/30 px-4 py-1.5 rounded-lg font-medium text-sm transition-colors whitespace-nowrap"
          >
            Zobacz plany
          </a>
          <button
            onClick={() => setIsDismissed(true)}
            className="text-blue-200 hover:text-white p-1"
            aria-label="Zamknij"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
